import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Box,
  Paper,
  Breadcrumbs,
  Link,
  TextField,
  InputAdornment,
  IconButton,
  Tooltip,
  Button,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Collapse,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Snackbar,
  Alert,
  Divider,
  Badge
} from '@mui/material';
import {
  Folder as FolderIcon,
  FolderOpen as FolderOpenIcon,
  Description as DescriptionIcon,
  Home as HomeIcon,
  NavigateNext as NavigateNextIcon,
  Search as SearchIcon,
  CloudDownload as CloudDownloadIcon,
  CloudUpload as CloudUploadIcon,
  Visibility as VisibilityIcon,
  ExpandMore as ExpandMoreIcon,
  ChevronRight as ChevronRightIcon,
  InsertDriveFile as FileIcon,
  PictureAsPdf as PdfIcon,
  Image as ImageIcon,
  TableChart as ExcelIcon,
  Article as WordIcon,
  Refresh as RefreshIcon,
  Close as CloseIcon,
  Person as PersonIcon,
  Comment as CommentIcon,
  History as HistoryIcon,
  FiberManualRecord as FiberManualRecordIcon,
  CheckCircle as CheckCircleIcon,
  ReportProblem as ReportProblemIcon,
  HourglassTop as HourglassTopIcon
} from '@mui/icons-material';
import api from '../../config/axiosConfig';
import { getQaStatusInfo } from '../../utils/documentQaStatus';

// --- Tree Node Component ---
// generatedFolders/openFindingsFolders vienen precalculados desde qaByPath (ver
// buildFolderIndicators) recorriendo TODOS los dropboxPath conocidos, no solo los de las carpetas
// ya expandidas -- por eso el badge aparece aunque el nodo nunca se haya abierto.
const TreeNode = ({ node, level, selectedPath, onSelectFolder, onToggle, expandedNodes, generatedFolders, openFindingsFolders, pendingReviewFolders, showQa }) => {
  const isExpanded = expandedNodes[node.path] || false;
  const isSelected = selectedPath === node.path;
  const hasChildren = node.children && node.children.length > 0;
  const hasLoadedChildren = node.childrenLoaded;
  const isGenerated = generatedFolders.has(node.path);
  const openFindings = openFindingsFolders.get(node.path) || 0;
  const pendingReview = pendingReviewFolders.has(node.path);

  const handleToggle = (e) => {
    e.stopPropagation();
    onToggle(node.path);
  };

  return (
    <>
      <ListItemButton
        onClick={() => onSelectFolder(node)}
        selected={isSelected}
        sx={{
          pl: 1 + level * 2,
          py: 0.4,
          minHeight: 32,
          borderRadius: 1,
          mx: 0.5,
          mb: 0.2,
          '&.Mui-selected': {
            bgcolor: 'primary.main',
            color: 'white',
            '&:hover': { bgcolor: 'primary.dark' },
            '& .MuiListItemIcon-root': { color: 'white' },
            '& .MuiListItemText-primary': { color: 'white' },
          },
          '&:hover': { bgcolor: 'action.hover' },
        }}
      >
        <IconButton
          size="small"
          onClick={handleToggle}
          sx={{ p: 0, mr: 0.5, minWidth: 20, color: isSelected ? 'white' : 'text.secondary' }}
        >
          {hasChildren || !hasLoadedChildren ? (
            isExpanded ? <ExpandMoreIcon sx={{ fontSize: 18 }} /> : <ChevronRightIcon sx={{ fontSize: 18 }} />
          ) : (
            <Box sx={{ width: 18 }} />
          )}
        </IconButton>
        <ListItemIcon sx={{ minWidth: 28, color: isSelected ? 'white' : '#e8a817' }}>
          {isExpanded ? <FolderOpenIcon sx={{ fontSize: 20 }} /> : <FolderIcon sx={{ fontSize: 20 }} />}
        </ListItemIcon>
        <ListItemText
          primary={node.name}
          primaryTypographyProps={{
            variant: 'body2',
            noWrap: true,
            sx: { fontSize: '0.82rem', fontWeight: isSelected ? 600 : 400 }
          }}
          sx={{ flex: isGenerated ? '0 1 auto' : 1 }}
        />
        {isGenerated && (
          openFindings > 0 ? (
            <Tooltip title={`${openFindings} nota${openFindings > 1 ? 's' : ''} de calidad sin resolver`}>
              <Badge
                badgeContent={openFindings}
                max={99}
                sx={{
                  ml: 0.5, flexShrink: 0,
                  '& .MuiBadge-badge': {
                    fontSize: '0.6rem', fontWeight: 700, height: 14, minWidth: 14, right: -3, top: 2,
                    bgcolor: isSelected ? 'white' : '#c62828',
                    color: isSelected ? '#c62828' : 'white',
                  }
                }}
              >
                <ReportProblemIcon sx={{ fontSize: 18, color: isSelected ? 'white' : '#e53935' }} />
              </Badge>
            </Tooltip>
          ) : (
            pendingReview ? (
              <Tooltip title="Generado — pendiente de revisión de calidad">
                <HourglassTopIcon sx={{ fontSize: 18, color: isSelected ? 'white' : '#f9a825', ml: 0.5, flexShrink: 0 }} />
              </Tooltip>
            ) : (
              <Tooltip title={node.name === 'constancias'
                ? 'Constancias generadas'
                : showQa ? 'Documentos generados — sin notas de calidad pendientes' : 'Documentos generados'}>
                <CheckCircleIcon sx={{ fontSize: 18, color: isSelected ? 'white' : '#2e7d32', ml: 0.5, flexShrink: 0 }} />
              </Tooltip>
            )
          )
        )}
      </ListItemButton>
      <Collapse in={isExpanded} timeout="auto" unmountOnExit>
        {node.children && node.children.filter(c => c.isFolder).map(child => (
          <TreeNode
            key={child.path}
            node={child}
            level={level + 1}
            selectedPath={selectedPath}
            onSelectFolder={onSelectFolder}
            onToggle={onToggle}
            expandedNodes={expandedNodes}
            generatedFolders={generatedFolders}
            openFindingsFolders={openFindingsFolders}
            pendingReviewFolders={pendingReviewFolders}
            showQa={showQa}
          />
        ))}
      </Collapse>
    </>
  );
};

// --- File icon helper ---
const getFileIcon = (filename) => {
  if (!filename) return <FileIcon />;
  const ext = filename.split('.').pop().toLowerCase();
  if (['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp', 'svg'].includes(ext))
    return <ImageIcon sx={{ color: '#4caf50' }} />;
  if (ext === 'pdf')
    return <PdfIcon sx={{ color: '#e53935' }} />;
  if (['xls', 'xlsx', 'csv'].includes(ext))
    return <ExcelIcon sx={{ color: '#2e7d32' }} />;
  if (['doc', 'docx'].includes(ext))
    return <WordIcon sx={{ color: '#1565c0' }} />;
  return <FileIcon sx={{ color: '#757575' }} />;
};

// Mismo criterio que App.js (QaRoute) y DocumentQaController: ver/navegar a Revisión de Calidad
// es solo Admin/Supervisor de Calidad (isQaAuthorized en backend). Reemplazar es más permisivo
// -- también Coordinador/Ejecutivo de Operaciones (isReplaceAuthorized en backend), porque son
// quienes generan/gestionan el reporte de sus entes. Dos sets de roles distintos, cada uno
// reflejando exactamente lo que el backend ya exige -- para no mostrar una acción que el backend
// va a rechazar con 403.
const QA_VIEW_ROLES = ['ADMINISTRADOR', 'SUPERVISOR_CALIDAD'];
const QA_REPLACE_ROLES = ['ADMINISTRADOR', 'SUPERVISOR_CALIDAD', 'COORDINADOR_OPERACIONES', 'EJECUTIVO_OPERACIONES'];
const getUserInfo = () => {
  try {
    return JSON.parse(sessionStorage.getItem('userInfo') || '{}');
  } catch {
    return {};
  }
};
const getUserRoleName = () => {
  const role = getUserInfo()?.role;
  return (typeof role === 'string' ? role : role?.name) || '';
};

// Progreso simulado para la subida a Dropbox: no hay forma de medir el avance real del tramo
// backend->Dropbox. Avanza por pasos aleatorios que se hacen más chicos según se acerca al tope
// de 95% -- solo el 100% real llega al terminar la petición (ver handleReplaceFile).
const startFakeProgress = (setProgress) => {
  setProgress(0);
  return setInterval(() => {
    setProgress(prev => {
      if (prev >= 95) return prev;
      const remaining = 95 - prev;
      const step = Math.max(1, Math.round(Math.random() * remaining * 0.15));
      return Math.min(95, prev + step);
    });
  }, 450);
};

// Polling que se pausa por completo mientras la pestaña está en segundo plano (document.hidden)
// o `enabled` es false -- así no se sigue golpeando el backend cuando nadie está mirando esta
// pantalla (pestaña minimizada/oculta) ni cuando la pantalla/diálogo que lo usa está cerrado.
// Al desmontar el componente (salir de esta pantalla) React limpia el effect y todo se detiene
// por completo, sin importar la visibilidad.
const useVisiblePolling = (callback, intervalMs, enabled = true) => {
  useEffect(() => {
    if (!enabled) return undefined;
    let interval = null;
    const start = () => { if (!interval) interval = setInterval(callback, intervalMs); };
    const stop = () => { if (interval) { clearInterval(interval); interval = null; } };
    const onVisibility = () => {
      if (document.visibilityState === 'visible') {
        callback();
        start();
      } else {
        stop();
      }
    };
    if (document.visibilityState === 'visible') {
      callback();
      start();
    }
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      stop();
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [callback, intervalMs, enabled]);
};

// Etiqueta corta por documentKey para los diálogos de Notas/Versiones -- antes era un ternario
// PIPC/RIC que asumía que todo lo que no fuera PIPC era RIC, lo cual rotularía mal un Acta
// Constitutiva (u otro tipo futuro) como "RIC".
const DOCUMENT_KEY_LABELS = {
  PIPC: 'PIPC',
  REPORTE_INSPECCION: 'RIC',
  ACTA_CONSTITUTIVA_UIPC: 'Acta Constitutiva',
  CARTA_CORRESPONSABILIDAD: 'Carta de Corresponsabilidad'
};
const getDocumentKeyLabel = (documentKey) => DOCUMENT_KEY_LABELS[documentKey]
  || (documentKey?.startsWith('CONSTANCIAS_CURSO_') ? 'Constancias' : documentKey) || '';

const CentroGestionDocumental = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const userRoleName = getUserRoleName().toUpperCase();
  const currentUserId = getUserInfo()?.id;
  const canViewQa = QA_VIEW_ROLES.includes(userRoleName);
  const canReplaceQa = QA_REPLACE_ROLES.includes(userRoleName);
  // Coordinador/Admin/Calidad sin restricción (mismo criterio que
  // AccessControlService.UNRESTRICTED_ROLES); un Ejecutivo solo puede reemplazar documentos de
  // SUS entes asignados -- si no coincide, mejor no mostrar el botón que mostrarlo y que el
  // backend lo rechace con 403.
  const canReplaceDoc = (qaDoc) => {
    if (!canReplaceQa || !qaDoc) return false;
    if (userRoleName !== 'EJECUTIVO_OPERACIONES') return true;
    return qaDoc.assignedUserId != null && String(qaDoc.assignedUserId) === String(currentUserId);
  };
  const [qaByPath, setQaByPath] = useState({});
  // Documentos sobre los que Coordinador/Ejecutivo pueden actuar (reemplazar, notas, versiones),
  // separado de qaByPath para no mostrarles los indicadores de Revisión de Calidad.
  const [actionableByPath, setActionableByPath] = useState({});
  // Acta Constitutiva / Carta de Corresponsabilidad: reemplazables, pero sin Revisión de Calidad.
  const [supportingByPath, setSupportingByPath] = useState({});
  const docFor = (path) => qaByPath[path] || actionableByPath[path] || supportingByPath[path];
  const [generatedFolderList, setGeneratedFolderList] = useState([]);
  const [treeData, setTreeData] = useState([]);
  const [expandedNodes, setExpandedNodes] = useState({});
  const [selectedPath, setSelectedPath] = useState('');
  const [currentItems, setCurrentItems] = useState([]);
  const [currentPath, setCurrentPath] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [contentLoading, setContentLoading] = useState(false);
  const [error, setError] = useState(null);
  const [previewFile, setPreviewFile] = useState(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [wordHtml, setWordHtml] = useState(null);
  // Reemplazo manual de RIC/PIPC (descargar, editar fuera del sistema, volver a subir con el
  // mismo nombre) -- misma lógica y mismo endpoint que ya existen en Revisión de Calidad, solo
  // que acá se dispara desde el explorador de Dropbox. Aplica a los documentos generados por el
  // sistema (RIC/PIPC y Acta/Carta, con quotationDocumentId); documentos de cumplimiento, logo,
  // fotos y constancias se gestionan desde sus propias pantallas.
  const [replacingPath, setReplacingPath] = useState(null);
  const [replaceProgress, setReplaceProgress] = useState(0);
  const [replaceSnackbar, setReplaceSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const replaceInputRef = useRef(null);
  const [pendingReplaceItem, setPendingReplaceItem] = useState(null);

  // Cuadro de notas (solo lectura acá) de un RIC/PIPC -- mismas notas que se ven/escriben en
  // Revisión de Calidad, expuestas también en Gestión Documental para quien tenga permiso de
  // reemplazar ese documento (Admin/Calidad/Coordinador/Ejecutivo-asignado, backend ya lo valida
  // igual en GET /document-qa/{id}/notes).
  const [notesDialogOpen, setNotesDialogOpen] = useState(false);
  const [notesDialogDoc, setNotesDialogDoc] = useState(null);
  const [notesLoading, setNotesLoading] = useState(false);
  const [notes, setNotes] = useState([]);
  const [resolvingNoteId, setResolvingNoteId] = useState(null);
  // Hallazgos por criterio (A01, A02...) del checklist FR-O-07 -- son los que realmente
  // alimentan el ícono rojo del árbol (openFindingsCount), a diferencia de las notas de texto
  // libre de arriba. Se muestran juntos acá para que "Notas" sea la fuente completa de lo
  // pendiente de un documento, evitando la confusión de ver el ícono rojo con todas las notas
  // de texto ya resueltas.
  const [findings, setFindings] = useState([]);
  const [findingsLoading, setFindingsLoading] = useState(false);
  const [resolvingFindingCode, setResolvingFindingCode] = useState(null);

  // Trae notas + hallazgos de un documento sin tocar los flags de loading -- usado tanto en la
  // carga inicial del diálogo como en el auto-refresh en segundo plano (polling/foco), donde no
  // queremos que la lista parpadee a "Sin notas" mientras se re-consulta.
  const fetchNotesAndFindings = useCallback(async (qaDoc) => {
    try {
      const res = await api.get(`/document-qa/${qaDoc.quotationDocumentId}/notes`);
      setNotes(res.data || []);
    } catch (err) {
      setNotes([]);
    }
    try {
      const res = await api.get(`/document-qa/${qaDoc.quotationDocumentId}/checklist`);
      setFindings((res.data || [])
        .filter(item => item.finding)
        .map(item => ({ ...item.finding, code: item.code, sectionLabel: item.sectionLabel, criterionDescription: item.description })));
    } catch (err) {
      setFindings([]);
    }
  }, []);

  const handleOpenNotes = async (qaDoc) => {
    setNotesDialogDoc(qaDoc);
    setNotesDialogOpen(true);
    setNotesLoading(true);
    setFindingsLoading(true);
    await fetchNotesAndFindings(qaDoc);
    setNotesLoading(false);
    setFindingsLoading(false);
  };

  // Auto-refresh de notas/hallazgos MIENTRAS el diálogo está abierto Y la pestaña está visible:
  // otro usuario (Supervisor de Calidad, Ejecutivo) puede resolver algo desde otra sesión
  // mientras este documento sigue abierto acá. Se detiene solo con cerrar el diálogo, cambiar de
  // pestaña, o salir de esta pantalla -- ver useVisiblePolling.
  const tickNotesAndFindings = useCallback(() => {
    if (notesDialogDoc) fetchNotesAndFindings(notesDialogDoc);
  }, [notesDialogDoc, fetchNotesAndFindings]);
  useVisiblePolling(tickNotesAndFindings, 15000, notesDialogOpen && !!notesDialogDoc);

  // Solo Admin/Supervisor de Calidad (mismos roles que pueden escribir una nota) pueden marcarla
  // como resuelta -- backend lo valida igual (DocumentQaController.resolveNote).
  const handleResolveNote = async (note) => {
    if (!notesDialogDoc) return;
    setResolvingNoteId(note.id);
    try {
      const res = await api.post(`/document-qa/${notesDialogDoc.quotationDocumentId}/notes/${note.id}/resolve`);
      setNotes(prev => prev.map(n => n.id === note.id ? res.data : n));
      refreshQaByPath();
    } catch (err) {
      setReplaceSnackbar({ open: true, severity: 'error', message: err.response?.data?.error || 'Error al marcar la nota como resuelta' });
    } finally {
      setResolvingNoteId(null);
    }
  };

  const FINDING_STATUS_LABELS = {
    CORRECCION_REQUERIDA: 'Corrección requerida',
    NICE_TO_HAVE: 'Nice to have',
    NA: 'No aplica',
    RVH: 'RVH',
  };

  // Mismo permiso que resolveFinding en el backend (Admin/Supervisor Calidad).
  const handleResolveFinding = async (finding) => {
    if (!notesDialogDoc) return;
    setResolvingFindingCode(finding.criteriaCode);
    try {
      const res = await api.post(`/document-qa/${notesDialogDoc.quotationDocumentId}/findings/${finding.criteriaCode}/resolve`);
      setFindings(prev => prev.map(f => f.criteriaCode === finding.criteriaCode
        ? { ...f, ...res.data, code: f.code, sectionLabel: f.sectionLabel, criterionDescription: f.criterionDescription }
        : f));
      refreshQaByPath();
    } catch (err) {
      setReplaceSnackbar({ open: true, severity: 'error', message: err.response?.data?.error || 'Error al marcar el hallazgo como resuelto' });
    } finally {
      setResolvingFindingCode(null);
    }
  };

  // Historial de versiones reemplazadas de un RIC/PIPC -- mismo acceso que Notas (backend valida
  // igual en GET /document-qa/{id}/versions).
  const [versionsDialogOpen, setVersionsDialogOpen] = useState(false);
  const [versionsDialogDoc, setVersionsDialogDoc] = useState(null);
  const [versionsLoading, setVersionsLoading] = useState(false);
  const [versions, setVersions] = useState([]);

  const handleOpenVersions = async (qaDoc) => {
    setVersionsDialogDoc(qaDoc);
    setVersionsDialogOpen(true);
    setVersionsLoading(true);
    try {
      const res = await api.get(`/document-qa/${qaDoc.quotationDocumentId}/versions`);
      setVersions(res.data || []);
    } catch (err) {
      setVersions([]);
    } finally {
      setVersionsLoading(false);
    }
  };

  // Fuerza descarga directa en vez de la vista previa de Dropbox (dl=0 -> dl=1)
  const getVersionDownloadLink = (dropboxLink) => {
    if (!dropboxLink) return null;
    if (/[?&]dl=1/.test(dropboxLink)) return dropboxLink;
    if (/[?&]dl=0/.test(dropboxLink)) return dropboxLink.replace(/dl=0/, 'dl=1');
    return dropboxLink + (dropboxLink.includes('?') ? '&dl=1' : '?dl=1');
  };

  const formatVersionFileSize = (bytes) => {
    if (bytes == null) return '';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  // --- Fetch folder contents ---
  const fetchFolderContents = useCallback(async (path) => {
    try {
      const response = await api.get('/dropbox/list-folder', {
        params: { path: path || '' }
      });
      if (response.data.success) {
        return response.data.items.map(item => ({
          id: item.path,
          name: item.name,
          type: item.isFolder ? 'folder' : 'file',
          count: item.fileCount || 0,
          size: item.size || 0,
          lastModified: item.lastModified || '',
          path: item.path,
          isFolder: item.isFolder,
          children: [],
          childrenLoaded: false
        }));
      }
      return [];
    } catch (err) {
      console.error('Error fetching folder:', err);
      return [];
    }
  }, []);

  // --- Load root (o directo a una carpeta específica si llegamos con state.initialPath,
  // ej. desde "Ver en Gestión Documental" tras generar un RIC/PIPC) ---
  useEffect(() => {
    const loadRoot = async () => {
      setLoading(true);
      const items = await fetchFolderContents('');
      const folders = items.filter(i => i.isFolder);
      setTreeData(folders);

      const initialPath = location.state?.initialPath;
      if (initialPath) {
        const parts = initialPath.split('/').filter(p => p);
        setSelectedPath(initialPath);
        setCurrentPath(parts);
        setContentLoading(true);
        const targetItems = await fetchFolderContents(initialPath);
        setCurrentItems(targetItems);
        setContentLoading(false);
      } else {
        setCurrentItems(items);
      }
      setLoading(false);
    };
    loadRoot();
  }, [fetchFolderContents]);

  // --- Estado de revisión de calidad de los PIPC/RIC generados, indexado por ruta de Dropbox --
  // se re-consulta también al resolver una nota o hallazgo desde el diálogo de Notas, para que
  // el ícono del árbol (que depende de openFindingsCount) se actualice sin recargar la página.
  const refreshQaByPath = useCallback(() => {
    api.get('/generated-documents/folders')
      .then(res => setGeneratedFolderList(Array.isArray(res.data) ? res.data : []))
      .catch(() => setGeneratedFolderList([]));
    if (canReplaceQa) {
      api.get('/document-qa/supporting').then(res => {
        const map = {};
        (res.data || []).forEach(doc => {
          if (doc.dropboxPath) map[doc.dropboxPath] = doc;
        });
        setSupportingByPath(map);
      }).catch(() => setSupportingByPath({}));
    }
    if (!canViewQa) {
      setQaByPath({});
      if (!canReplaceQa) return Promise.resolve();
      return api.get('/document-qa/actionable').then(res => {
        const map = {};
        (res.data || []).forEach(doc => {
          if (doc.dropboxPath) map[doc.dropboxPath] = doc;
        });
        setActionableByPath(map);
      }).catch(() => setActionableByPath({}));
    }
    return api.get('/document-qa').then(res => {
      const map = {};
      (res.data || []).forEach(doc => {
        if (doc.dropboxPath) map[doc.dropboxPath] = doc;
      });
      setQaByPath(map);
    }).catch(() => setQaByPath({}));
  }, [canViewQa, canReplaceQa]);

  // Auto-refresh entre usuarios: otro usuario puede resolver una nota/hallazgo, reemplazar un
  // documento o aprobar/rechazar una ronda desde su propia sesión mientras esta pantalla sigue
  // abierta acá. Se pausa solo con cambiar de pestaña o salir de esta pantalla -- ver
  // useVisiblePolling (cero requests en pestaña oculta o pantalla desmontada).
  useVisiblePolling(refreshQaByPath, 20000);

  // --- Indicadores de carpeta en el árbol: "ya generado" (punto verde) y "notas/hallazgos sin
  // resolver" (chip rojo con contador) -- se calculan de qaByPath (ya cargado completo, no
  // depende de qué carpetas estén expandidas) marcando TODAS las carpetas ancestro de cada
  // archivo generado (reportes, documentos_generados, Matriz/Sucursal, empresa) para que la
  // alerta de notas pendientes se vea "burbujeada" hacia arriba sin importar desde qué nivel
  // se esté navegando -- no solo en la carpeta inmediata del archivo.
  const { generatedFolders, openFindingsFolders, pendingReviewFolders } = React.useMemo(() => {
    const generated = new Set();
    const openCounts = new Map();
    const pending = new Set();
    const ancestorsOf = (path, includeSelf) => {
      const segments = path.split('/').filter(Boolean);
      const last = includeSelf ? segments.length : segments.length - 1;
      const out = [];
      for (let i = 1; i <= last; i++) out.push('/' + segments.slice(0, i).join('/'));
      return out;
    };
    generatedFolderList.forEach(folderPath => {
      ancestorsOf(folderPath, true).forEach(p => generated.add(p));
    });
    Object.values(qaByPath).forEach(doc => {
      if (!doc.dropboxPath) return;
      const openCount = doc.openFindingsCount || 0;
      const isPending = doc.qaStatus === 'PENDIENTE_REVISION';
      ancestorsOf(doc.dropboxPath, false).forEach(p => {
        generated.add(p);
        if (openCount > 0) openCounts.set(p, (openCounts.get(p) || 0) + openCount);
        if (isPending) pending.add(p);
      });
    });
    return { generatedFolders: generated, openFindingsFolders: openCounts, pendingReviewFolders: pending };
  }, [qaByPath, generatedFolderList]);

  // --- Toggle expand/collapse tree node ---
  const handleToggle = async (path) => {
    const isCurrentlyExpanded = expandedNodes[path];
    setExpandedNodes(prev => ({ ...prev, [path]: !isCurrentlyExpanded }));

    if (!isCurrentlyExpanded) {
      // Load children if not loaded
      const node = findNode(treeData, path);
      if (node && !node.childrenLoaded) {
        const children = await fetchFolderContents(path);
        const folderChildren = children.filter(c => c.isFolder);
        updateNodeChildren(path, folderChildren);
      }
    }
  };

  // --- Find node in tree ---
  const findNode = (nodes, path) => {
    for (const node of nodes) {
      if (node.path === path) return node;
      if (node.children) {
        const found = findNode(node.children, path);
        if (found) return found;
      }
    }
    return null;
  };

  // --- Update node children in tree ---
  const updateNodeChildren = (path, children) => {
    setTreeData(prev => updateTreeChildren(prev, path, children));
  };

  const updateTreeChildren = (nodes, targetPath, children) => {
    return nodes.map(node => {
      if (node.path === targetPath) {
        return { ...node, children, childrenLoaded: true };
      }
      if (node.children) {
        return { ...node, children: updateTreeChildren(node.children, targetPath, children) };
      }
      return node;
    });
  };

  // --- Select folder (loads content in right panel) ---
  const handleSelectFolder = async (node) => {
    setSelectedPath(node.path);
    setContentLoading(true);
    setError(null);

    // Build breadcrumb path
    const parts = node.path.split('/').filter(p => p);
    setCurrentPath(parts);

    // Load folder contents for right panel
    const items = await fetchFolderContents(node.path);
    setCurrentItems(items);
    setContentLoading(false);

    // Also expand the tree node and load children
    if (!expandedNodes[node.path]) {
      setExpandedNodes(prev => ({ ...prev, [node.path]: true }));
    }
    if (!node.childrenLoaded) {
      const folderChildren = items.filter(c => c.isFolder);
      updateNodeChildren(node.path, folderChildren);
    }
  };

  // --- Go to root ---
  const handleGoRoot = async () => {
    setSelectedPath('');
    setCurrentPath([]);
    setContentLoading(true);
    const items = await fetchFolderContents('');
    setCurrentItems(items);
    setContentLoading(false);
  };

  // --- Breadcrumb navigation ---
  const handleBreadcrumbClick = async (index) => {
    if (index === -1) {
      handleGoRoot();
    } else {
      const parts = currentPath.slice(0, index + 1);
      const path = '/' + parts.join('/');
      setSelectedPath(path);
      setCurrentPath(parts);
      setContentLoading(true);
      const items = await fetchFolderContents(path);
      setCurrentItems(items);
      setContentLoading(false);
    }
  };

  // --- Helpers ---
  const formatBytes = (bytes) => {
    if (!bytes || bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
  };

  const isImage = (filename) => /\.(jpe?g|png|gif|webp|bmp)$/i.test(filename || '');
  const isPDF = (filename) => /\.pdf$/i.test(filename || '');
  const isWord = (filename) => /\.docx?$/i.test(filename || '');
  const isPPTX = (filename) => /\.pptx?$/i.test(filename || '');

  // --- Preview ---
  const handlePreviewFile = async (file) => {
    setPreviewFile(file);
    setPreviewOpen(true);
    setPreviewLoading(true);
    setPreviewUrl(null);
    setWordHtml(null);

    // La conversión de Word a HTML (mammoth) pierde demasiado formato -- header/footer,
    // paginación, tablas complejas -- y termina confundiendo más de lo que ayuda. Mejor no
    // mostrar nada acá y que el usuario descargue el archivo real (cae al fallback
    // "Vista previa no disponible" + botón Descargar que ya existe más abajo).
    if (isWord(file.name)) {
      setPreviewLoading(false);
      return;
    }

    try {
      const response = await api.get('/dropbox/preview-file', {
        params: { path: file.path },
        responseType: 'blob'
      });
      if (isPPTX(file.name)) {
        const pngResponse = await api.get('/constancias/preview-pptx', {
          params: { path: file.path },
          responseType: 'blob'
        });
        const blobUrl = window.URL.createObjectURL(new Blob([pngResponse.data], { type: 'image/png' }));
        setPreviewUrl(blobUrl);
      } else {
        const blobUrl = window.URL.createObjectURL(new Blob([response.data], { type: response.headers['content-type'] }));
        setPreviewUrl(blobUrl);
      }
    } catch (err) {
      console.error('Error cargando preview:', err);
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleClosePreview = () => {
    setPreviewOpen(false);
    setPreviewFile(null);
    setWordHtml(null);
    if (previewUrl) {
      window.URL.revokeObjectURL(previewUrl);
      setPreviewUrl(null);
    }
  };

  // --- Download ---
  const handleDownloadFile = async (file) => {
    try {
      const response = await api.get('/dropbox/download-file', {
        params: { path: file.path },
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', file.name);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Error downloading file:', err);
      setError('Error al descargar: ' + (err.response?.data?.message || err.message));
    }
  };

  const handleReplaceClick = (item) => {
    setPendingReplaceItem(item);
    replaceInputRef.current?.click();
  };

  const handleReplaceFile = async (e) => {
    const file = e.target.files[0];
    e.target.value = '';
    const item = pendingReplaceItem;
    setPendingReplaceItem(null);
    const qaDoc = item ? docFor(item.path) : null;
    if (!file || !qaDoc) return;

    setReplacingPath(item.path);
    const progressTimer = startFakeProgress(setReplaceProgress);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await api.post(`/document-qa/${qaDoc.quotationDocumentId}/replace`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
        // Los PIPC/RIC pueden pesar decenas/hasta ~150MB (límite configurado) -- 30-60s no
        // alcanza para subir eso en una conexión lenta.
        timeout: 300000,
      });
      clearInterval(progressTimer);
      setReplaceProgress(100);
      if (res.data.qaApplicable === false) {
        setSupportingByPath(prev => ({ ...prev, [item.path]: res.data }));
      } else if (canViewQa) {
        setQaByPath(prev => ({ ...prev, [item.path]: res.data }));
      } else {
        setActionableByPath(prev => ({ ...prev, [item.path]: res.data }));
      }
      setReplaceSnackbar(res.data.replaced === false
        ? { open: true, severity: 'info', message: 'No se reemplazó: el archivo subido es idéntico al vigente.' }
        : {
            open: true,
            severity: 'success',
            message: res.data.contentChanged === true
              ? 'Documento reemplazado — se detectó un cambio de contenido'
              : 'Documento reemplazado correctamente',
          });
    } catch (err) {
      clearInterval(progressTimer);
      setReplaceSnackbar({
        open: true, severity: 'error',
        message: err.response?.data?.error || 'Error al reemplazar el documento',
      });
    } finally {
      setReplacingPath(null);
    }
  };

  // --- Refresh ---
  const handleRefresh = async () => {
    setLoading(true);
    setExpandedNodes({});
    const items = await fetchFolderContents('');
    const folders = items.filter(i => i.isFolder);
    setTreeData(folders);
    setCurrentItems(items);
    setSelectedPath('');
    setCurrentPath([]);
    setLoading(false);
  };

  // --- Filter items by search ---
  const filteredItems = searchTerm
    ? currentItems.filter(i => i.name.toLowerCase().includes(searchTerm.toLowerCase()))
    : currentItems;

  const folderItems = filteredItems.filter(i => i.isFolder);
  const fileItems = filteredItems.filter(i => !i.isFolder);

  return (
    <Container maxWidth="xl" sx={{ py: 3 }}>
      {/* Header */}
      <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box>
          <Typography
            variant="h5"
            sx={{
              fontWeight: 700,
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent'
            }}
          >
            Centro de Gestión Documental
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Navegue por la estructura de documentos en Dropbox
          </Typography>
          <Paper 
            elevation={0} 
            sx={{ 
              mt: 2, 
              p: 2, 
              bgcolor: '#e3f2fd', 
              borderLeft: '4px solid #2196f3',
              borderRadius: 1
            }}
          >
            <Typography variant="body2" sx={{ fontWeight: 600, color: '#1565c0', mb: 0.5 }}>
              📌 Nota Importante
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Esta sección contiene <strong>documentación general de la empresa</strong>. 
              La documentación específica de cada entidad (sucursal o matriz) deberá subirse 
              en la sección correspondiente de cada una.
            </Typography>
          </Paper>
        </Box>
        <Tooltip title="Recargar">
          <IconButton onClick={handleRefresh} disabled={loading}>
            <RefreshIcon />
          </IconButton>
        </Tooltip>
      </Box>

      {/* Breadcrumbs */}
      <Paper elevation={0} sx={{ px: 2, py: 1, mb: 2, bgcolor: '#f5f5f5', borderRadius: 1 }}>
        <Breadcrumbs separator={<NavigateNextIcon fontSize="small" />} aria-label="breadcrumb">
          <Link
            component="button"
            variant="body2"
            onClick={() => handleBreadcrumbClick(-1)}
            underline="hover"
            sx={{ display: 'flex', alignItems: 'center', fontWeight: currentPath.length === 0 ? 600 : 400 }}
          >
            <HomeIcon sx={{ mr: 0.5, fontSize: 18 }} /> Dropbox
          </Link>
          {currentPath.map((part, idx) => (
            <Link
              key={idx}
              component="button"
              variant="body2"
              onClick={() => handleBreadcrumbClick(idx)}
              underline="hover"
              sx={{ fontWeight: idx === currentPath.length - 1 ? 600 : 400 }}
            >
              {part}
            </Link>
          ))}
        </Breadcrumbs>
      </Paper>

      {/* Main Layout: Tree + Content */}
      <Box sx={{ display: 'flex', gap: 2, height: 'calc(100vh - 240px)', minHeight: 500 }}>
        {/* LEFT: Tree Panel */}
        <Paper
          elevation={2}
          sx={{
            width: 420,
            minWidth: 320,
            maxWidth: 600,
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
            borderRadius: 2,
            border: '1px solid',
            borderColor: 'divider',
            resize: 'horizontal',
          }}
        >
          {/* Tree Header */}
          <Box sx={{
            px: 1.5, py: 1,
            bgcolor: '#1e293b',
            color: 'white',
            display: 'flex',
            alignItems: 'center',
            gap: 1
          }}>
            <FolderIcon sx={{ fontSize: 18, color: '#e8a817' }} />
            <Typography variant="subtitle2" sx={{ fontWeight: 600, fontSize: '0.82rem' }}>
              Explorador de Archivos
            </Typography>
          </Box>

          {/* Tree Root Item */}
          <Box sx={{ overflowY: 'auto', flex: 1, py: 0.5 }}>
            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress size={24} />
              </Box>
            ) : (
              <List dense disablePadding>
                {/* Root item */}
                <ListItemButton
                  selected={selectedPath === ''}
                  onClick={handleGoRoot}
                  sx={{
                    pl: 1, py: 0.4, minHeight: 32, borderRadius: 1, mx: 0.5, mb: 0.2,
                    '&.Mui-selected': {
                      bgcolor: 'primary.main', color: 'white',
                      '&:hover': { bgcolor: 'primary.dark' },
                      '& .MuiListItemIcon-root': { color: 'white' },
                    },
                  }}
                >
                  <ListItemIcon sx={{ minWidth: 28, color: selectedPath === '' ? 'white' : '#1565c0' }}>
                    <HomeIcon sx={{ fontSize: 20 }} />
                  </ListItemIcon>
                  <ListItemText
                    primary="Conem_sistema"
                    primaryTypographyProps={{ variant: 'body2', sx: { fontSize: '0.82rem', fontWeight: 600 } }}
                  />
                </ListItemButton>

                {/* Tree nodes */}
                {treeData.map(node => (
                  <TreeNode
                    key={node.path}
                    node={node}
                    level={1}
                    selectedPath={selectedPath}
                    onSelectFolder={handleSelectFolder}
                    onToggle={handleToggle}
                    expandedNodes={expandedNodes}
                    generatedFolders={generatedFolders}
                    openFindingsFolders={openFindingsFolders}
                    pendingReviewFolders={pendingReviewFolders}
                    showQa={canViewQa}
                  />
                ))}
              </List>
            )}
          </Box>
        </Paper>

        {/* RIGHT: Content Panel */}
        <Paper
          elevation={2}
          sx={{
            flex: 1,
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
            borderRadius: 2,
            border: '1px solid',
            borderColor: 'divider'
          }}
        >
          {/* Toolbar */}
          <Box sx={{
            px: 2, py: 1,
            bgcolor: '#f8fafc',
            borderBottom: '1px solid',
            borderColor: 'divider',
            display: 'flex',
            alignItems: 'center',
            gap: 2
          }}>
            <TextField
              size="small"
              placeholder="Buscar en esta carpeta..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: 18 }} /></InputAdornment>,
              }}
              sx={{ flex: 1, maxWidth: 350, '& .MuiOutlinedInput-root': { borderRadius: 1, bgcolor: 'white', height: 34 } }}
            />
            <Typography variant="caption" color="text.secondary">
              {folderItems.length} carpetas, {fileItems.length} archivos
            </Typography>
          </Box>

          {/* Content */}
          <Box sx={{ flex: 1, overflowY: 'auto', px: 0 }}>
            {contentLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                <CircularProgress size={32} />
              </Box>
            ) : error ? (
              <Box sx={{ p: 4, textAlign: 'center' }}>
                <Typography color="error">{error}</Typography>
              </Box>
            ) : filteredItems.length === 0 ? (
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', color: 'text.disabled' }}>
                <FolderOpenIcon sx={{ fontSize: 64, mb: 1 }} />
                <Typography variant="body1">Carpeta vacía</Typography>
                <Typography variant="caption">No hay elementos en esta ubicación</Typography>
              </Box>
            ) : (
              <>
                {/* Folders */}
                {folderItems.length > 0 && (
                  <Box>
                    <Typography variant="caption" sx={{ px: 2, pt: 1.5, pb: 0.5, display: 'block', color: 'text.secondary', fontWeight: 600, textTransform: 'uppercase', fontSize: '0.7rem', letterSpacing: 0.5 }}>
                      Carpetas ({folderItems.length})
                    </Typography>
                    {folderItems.map(item => (
                      <Box
                        key={item.id}
                        onClick={() => handleSelectFolder(item)}
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1.5,
                          px: 2,
                          py: 0.8,
                          cursor: 'pointer',
                          borderBottom: '1px solid',
                          borderColor: 'grey.100',
                          '&:hover': { bgcolor: '#e3f2fd' },
                          transition: 'background 0.15s'
                        }}
                      >
                        <FolderIcon sx={{ fontSize: 22, color: '#e8a817' }} />
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography variant="body2" noWrap sx={{ fontWeight: 500 }}>{item.name}</Typography>
                        </Box>
                        <Typography variant="caption" color="text.secondary" sx={{ flexShrink: 0 }}>
                          {item.count > 0 ? `${item.count} archivos` : ''}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                )}

                {/* Files */}
                {fileItems.length > 0 && (
                  <Box>
                    <Typography variant="caption" sx={{ px: 2, pt: 1.5, pb: 0.5, display: 'block', color: 'text.secondary', fontWeight: 600, textTransform: 'uppercase', fontSize: '0.7rem', letterSpacing: 0.5 }}>
                      Archivos ({fileItems.length})
                    </Typography>
                    {fileItems.map(item => (
                      <Box
                        key={item.id}
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1.5,
                          px: 2,
                          py: 0.8,
                          borderBottom: '1px solid',
                          borderColor: 'grey.100',
                          '&:hover': { bgcolor: '#f5f5f5' },
                          transition: 'background 0.15s'
                        }}
                      >
                        {getFileIcon(item.name)}
                        <Box sx={{ flex: 1, minWidth: 0, display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography variant="body2" noWrap sx={{ fontWeight: 400 }}>{item.name}</Typography>
                          {qaByPath[item.path] && (() => {
                            const qaInfo = getQaStatusInfo(qaByPath[item.path].qaStatus);
                            return (
                              <Tooltip title={canViewQa
                                ? 'Ver en Revisión de Calidad'
                                : 'Solo Administradores y Supervisores de Calidad pueden acceder a Revisión de Calidad'}>
                                <Chip
                                  label={qaInfo.label}
                                  size="small"
                                  onClick={canViewQa
                                    // Se manda la carpeta actual como state (no query) para que, si el usuario
                                    // usa el botón "Volver a Gestión Documental" de Revisión de Calidad, se
                                    // reabra exactamente acá -- ver RevisionCalidad.jsx (returnContext) y el
                                    // manejo de location.state.initialPath más arriba en este mismo archivo.
                                    ? () => navigate(`/servicios/revision-calidad?doc=${qaByPath[item.path].quotationDocumentId}`, {
                                        state: { returnPath: selectedPath }
                                      })
                                    : undefined}
                                  sx={{
                                    bgcolor: qaInfo.bg, color: qaInfo.color, fontWeight: 600,
                                    height: 20, fontSize: '0.68rem', flexShrink: 0,
                                    cursor: canViewQa ? 'pointer' : 'not-allowed',
                                    opacity: canViewQa ? 1 : 0.7
                                  }}
                                />
                              </Tooltip>
                            );
                          })()}
                          {qaByPath[item.path]?.contentChanged === true && (
                            <Tooltip title="Reemplazado manualmente con contenido distinto al anterior">
                              <Chip
                                label="Modificado"
                                size="small"
                                sx={{ bgcolor: '#fff3e0', color: '#e65100', fontWeight: 600, height: 20, fontSize: '0.68rem', flexShrink: 0 }}
                              />
                            </Tooltip>
                          )}
                          {qaByPath[item.path]?.assignedUserName && (
                            <Tooltip title={`Ejecutivo asignado al ente: ${qaByPath[item.path].assignedUserName}`}>
                              <Chip
                                icon={<PersonIcon sx={{ fontSize: '14px !important' }} />}
                                label={qaByPath[item.path].assignedUserName}
                                size="small"
                                sx={{
                                  bgcolor: '#ede7f6', color: '#4527a0', fontWeight: 600,
                                  height: 20, fontSize: '0.68rem', flexShrink: 0, maxWidth: 160,
                                  '& .MuiChip-label': { overflow: 'hidden', textOverflow: 'ellipsis' }
                                }}
                              />
                            </Tooltip>
                          )}
                        </Box>
                        <Typography variant="caption" color="text.secondary" sx={{ minWidth: 60, textAlign: 'right', flexShrink: 0 }}>
                          {formatBytes(item.size)}
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 0.5, flexShrink: 0 }}>
                          {(isImage(item.name) || isPDF(item.name) || isPPTX(item.name)) && (
                            <Tooltip title="Vista previa">
                              <IconButton
                                size="small"
                                onClick={() => handlePreviewFile(item)}
                                sx={{ p: 0.5, color: '#7c3aed', '&:hover': { bgcolor: '#ede9fe' } }}
                              >
                                <VisibilityIcon sx={{ fontSize: 18 }} />
                              </IconButton>
                            </Tooltip>
                          )}
                          <Tooltip title="Descargar">
                            <IconButton
                              size="small"
                              onClick={() => handleDownloadFile(item)}
                              sx={{ p: 0.5, color: '#2e7d32', '&:hover': { bgcolor: '#e8f5e9' } }}
                            >
                              <CloudDownloadIcon sx={{ fontSize: 18 }} />
                            </IconButton>
                          </Tooltip>
                          {canReplaceDoc(docFor(item.path)) && docFor(item.path).qaApplicable !== false && (
                            <Tooltip title="Ver notas de Revisión de Calidad">
                              <IconButton
                                size="small"
                                onClick={() => handleOpenNotes(docFor(item.path))}
                                sx={{ p: 0.5, color: '#5e35b1', '&:hover': { bgcolor: '#ede7f6' } }}
                              >
                                <CommentIcon sx={{ fontSize: 18 }} />
                              </IconButton>
                            </Tooltip>
                          )}
                          {canReplaceDoc(docFor(item.path)) && (
                            <Tooltip title="Ver versiones anteriores">
                              <IconButton
                                size="small"
                                onClick={() => handleOpenVersions(docFor(item.path))}
                                sx={{ p: 0.5, color: '#5e35b1', '&:hover': { bgcolor: '#ede7f6' } }}
                              >
                                <HistoryIcon sx={{ fontSize: 18 }} />
                              </IconButton>
                            </Tooltip>
                          )}
                          {canReplaceDoc(docFor(item.path)) && (
                            <Tooltip title={replacingPath === item.path
                              ? `Subiendo... ${replaceProgress}%`
                              : 'Descargar, editar fuera del sistema, y volver a subir con el mismo nombre'}>
                              <span>
                                <IconButton
                                  size="small"
                                  onClick={() => handleReplaceClick(item)}
                                  disabled={replacingPath === item.path}
                                  sx={{ p: 0.5, color: '#e65100', '&:hover': { bgcolor: '#fff3e0' } }}
                                >
                                  {replacingPath === item.path
                                    ? <CircularProgress size={16} />
                                    : <CloudUploadIcon sx={{ fontSize: 18 }} />}
                                </IconButton>
                              </span>
                            </Tooltip>
                          )}
                        </Box>
                      </Box>
                    ))}
                  </Box>
                )}
              </>
            )}
          </Box>
        </Paper>
      </Box>

      {/* Preview Modal */}
      <Dialog open={previewOpen} onClose={handleClosePreview} maxWidth="lg" fullWidth>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 1.5 }}>
          <Typography variant="subtitle1" fontWeight={600} noWrap>{previewFile?.name}</Typography>
          <IconButton onClick={handleClosePreview} size="small"><CloseIcon /></IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 0 }}>
          {previewFile && (
            <Box sx={{ minHeight: 400, display: 'flex', justifyContent: 'center', alignItems: 'center', bgcolor: '#fafafa' }}>
              {previewLoading ? (
                <CircularProgress />
              ) : wordHtml ? (
                <Box sx={{ width: '100%', height: '75vh', overflowY: 'auto', bgcolor: '#f0f0f0', display: 'flex', justifyContent: 'center' }}>
                  <Box
                    sx={{
                      width: '100%',
                      maxWidth: 820,
                      my: 3,
                      mx: 2,
                      px: 6,
                      py: 4,
                      bgcolor: '#fff',
                      boxShadow: '0 1px 4px rgba(0,0,0,0.15)',
                      fontFamily: '"Times New Roman", serif',
                      fontSize: '12pt',
                      lineHeight: 1.6,
                      '& h1, & h2, & h3, & h4': { fontWeight: 700, mt: 2, mb: 1 },
                      '& p': { mb: 1 },
                      '& table': { borderCollapse: 'collapse', width: '100%', mb: 2 },
                      '& td, & th': { border: '1px solid #ccc', padding: '4px 8px', verticalAlign: 'top' },
                      '& strong, & b': { fontWeight: 700 },
                    }}
                    dangerouslySetInnerHTML={{ __html: wordHtml }}
                  />
                </Box>
              ) : previewUrl && (isImage(previewFile.name) || isPPTX(previewFile.name)) ? (
                <img
                  src={previewUrl}
                  alt={previewFile.name}
                  style={{ maxWidth: '100%', maxHeight: '70vh', objectFit: 'contain' }}
                />
              ) : previewUrl && isPDF(previewFile.name) ? (
                <iframe
                  src={previewUrl}
                  title={previewFile.name}
                  style={{ width: '100%', height: '70vh', border: 'none' }}
                />
              ) : (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                  <DescriptionIcon sx={{ fontSize: 64, color: 'text.disabled', mb: 1 }} />
                  <Typography variant="body1" color="text.secondary" gutterBottom>
                    {isWord(previewFile.name)
                      ? 'Vista previa no disponible para Word. Descargá el archivo para verlo con el formato completo.'
                      : 'Vista previa no disponible'}
                  </Typography>
                  <Button
                    variant="contained"
                    size="small"
                    startIcon={<CloudDownloadIcon />}
                    onClick={() => { handleDownloadFile(previewFile); handleClosePreview(); }}
                  >
                    Descargar
                  </Button>
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 2, py: 1 }}>
          <Button onClick={handleClosePreview} size="small">Cerrar</Button>
          <Button variant="contained" size="small" startIcon={<CloudDownloadIcon />} onClick={() => handleDownloadFile(previewFile)}>
            Descargar
          </Button>
        </DialogActions>
      </Dialog>

      <input
        ref={replaceInputRef}
        type="file"
        accept=".docx"
        style={{ display: 'none' }}
        onChange={handleReplaceFile}
      />
      <Snackbar
        open={replaceSnackbar.open}
        autoHideDuration={5000}
        onClose={() => setReplaceSnackbar(s => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert severity={replaceSnackbar.severity} onClose={() => setReplaceSnackbar(s => ({ ...s, open: false }))}>
          {replaceSnackbar.message}
        </Alert>
      </Snackbar>

      {/* Notas de Revisión de Calidad -- las QA_NOTE (texto libre) se pueden marcar como
          resueltas acá (Admin/Calidad); para escribir nuevas notas o aprobar/rechazar hay que ir
          a la pantalla de Revisión de Calidad. */}
      <Dialog open={notesDialogOpen} onClose={() => setNotesDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 1.5, bgcolor: '#faf9fc' }}>
          <Box>
            <Typography variant="subtitle1" fontWeight={600}>
              <CommentIcon sx={{ fontSize: 18, verticalAlign: 'middle', mr: 1, color: '#5e35b1' }} />
              Notas
            </Typography>
            {notesDialogDoc && (
              <Typography variant="caption" color="text.secondary">
                {getDocumentKeyLabel(notesDialogDoc.documentKey)} — {notesDialogDoc.entityName} ({notesDialogDoc.companyName})
              </Typography>
            )}
          </Box>
          <IconButton onClick={() => setNotesDialogOpen(false)} size="small"><CloseIcon /></IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ bgcolor: '#fafafa' }}>
          {notesLoading || findingsLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}><CircularProgress size={22} /></Box>
          ) : (() => {
            const pendingNotesCount = notes.filter(n => n.eventType === 'QA_NOTE' && !n.resolved).length;
            const pendingFindingsCount = findings.filter(f => !f.resolved).length;
            const totalPending = pendingNotesCount + pendingFindingsCount;
            return (
              <>
                {totalPending > 0 && (
                  <Alert severity="warning" sx={{ mb: 2, py: 0.3 }}>
                    {totalPending} pendiente{totalPending > 1 ? 's' : ''} de resolver
                    {pendingFindingsCount > 0 && ` (${pendingFindingsCount} hallazgo${pendingFindingsCount > 1 ? 's' : ''} de criterio)`}
                  </Alert>
                )}
                <Typography variant="overline" color="text.secondary" sx={{ display: 'block', fontWeight: 700 }}>
                  Notas
                </Typography>
                {notes.length === 0 && (
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>Sin notas de texto libre.</Typography>
                )}
                {notes.map(n => n.eventType === 'QA_DOCUMENT_REPLACED' ? (
                  <Box key={n.id} sx={{ display: 'flex', alignItems: 'center', gap: 1, my: 1.5 }}>
                    <Divider sx={{ flex: 1 }} />
                    <Tooltip title={n.note}>
                      <Chip
                        label={`Documento reemplazado — ${new Date(n.createdAt).toLocaleString('es-MX')}`}
                        size="small"
                        sx={{ bgcolor: '#e3f2fd', color: '#01579b', fontWeight: 600, fontSize: '0.68rem' }}
                      />
                    </Tooltip>
                    <Divider sx={{ flex: 1 }} />
                  </Box>
                ) : (
                  <Box
                    key={n.id}
                    sx={{
                      mb: 1.5, p: 1.5, borderRadius: 1.5,
                      bgcolor: 'white',
                      border: '1px solid',
                      borderColor: n.eventType === 'QA_NOTE' && !n.resolved ? '#ffe0b2' : '#eee',
                      borderLeft: '4px solid',
                      borderLeftColor: n.eventType === 'QA_NOTE' ? (n.resolved ? '#66bb6a' : '#ff9800') : '#bdbdbd',
                    }}
                  >
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5, flexWrap: 'wrap', gap: 1 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                        <Typography variant="caption" fontWeight={600}>
                          {n.authorName || 'Sistema'}
                        </Typography>
                        {n.round && (
                          <Chip
                            label={`Ronda ${n.round} — ${n.decision === 'APROBADO' ? 'Aprobado' : 'Rechazado'}`}
                            size="small"
                            sx={{
                              height: 18, fontSize: '0.65rem',
                              bgcolor: n.decision === 'APROBADO' ? '#e8f5e9' : '#ffebee',
                              color: n.decision === 'APROBADO' ? '#2e7d32' : '#c62828'
                            }}
                          />
                        )}
                        {n.eventType === 'QA_NOTE' && (
                          <Chip
                            label={n.resolved ? 'Resuelta' : 'Pendiente'}
                            size="small"
                            sx={{
                              height: 18, fontSize: '0.65rem', fontWeight: 600,
                              bgcolor: n.resolved ? '#e8f5e9' : '#fff3e0',
                              color: n.resolved ? '#2e7d32' : '#e65100'
                            }}
                          />
                        )}
                      </Box>
                      <Typography variant="caption" color="text.secondary">{new Date(n.createdAt).toLocaleString('es-MX')}</Typography>
                    </Box>
                    <Typography variant="body2">{n.note}</Typography>
                    {n.eventType === 'QA_NOTE' && n.resolved && (
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                        Resuelta por {n.resolvedByName || 'Sistema'} — {new Date(n.resolvedAt).toLocaleString('es-MX')}
                      </Typography>
                    )}
                    {n.eventType === 'QA_NOTE' && !n.resolved && canViewQa && (
                      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 0.5 }}>
                        <Button
                          size="small"
                          startIcon={resolvingNoteId === n.id ? <CircularProgress size={12} /> : <FiberManualRecordIcon sx={{ fontSize: 12 }} />}
                          disabled={resolvingNoteId === n.id}
                          onClick={() => handleResolveNote(n)}
                          sx={{ textTransform: 'none', fontSize: '0.72rem', py: 0.2 }}
                        >
                          Marcar como resuelta
                        </Button>
                      </Box>
                    )}
                  </Box>
                ))}

                <Divider sx={{ my: 2 }} />
                <Typography variant="overline" color="text.secondary" sx={{ display: 'block', fontWeight: 700 }}>
                  Hallazgos por criterio
                </Typography>
                {findings.length === 0 ? (
                  <Typography variant="body2" color="text.secondary">Sin hallazgos registrados en el checklist.</Typography>
                ) : findings.map(f => (
                  <Box
                    key={f.criteriaCode}
                    sx={{
                      mb: 1.5, p: 1.5, borderRadius: 1.5,
                      bgcolor: 'white',
                      border: '1px solid',
                      borderColor: !f.resolved ? '#ffe0b2' : '#eee',
                      borderLeft: '4px solid',
                      borderLeftColor: f.resolved ? '#66bb6a' : '#ff9800',
                    }}
                  >
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5, flexWrap: 'wrap', gap: 1 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                        <Chip
                          label={f.criteriaCode}
                          size="small"
                          sx={{ height: 18, fontSize: '0.65rem', fontWeight: 700, bgcolor: '#ede7f6', color: '#4527a0' }}
                        />
                        <Typography variant="caption" fontWeight={600}>
                          {FINDING_STATUS_LABELS[f.status] || f.status}
                        </Typography>
                        <Chip
                          label={f.resolved ? 'Resuelta' : 'Pendiente'}
                          size="small"
                          sx={{
                            height: 18, fontSize: '0.65rem', fontWeight: 600,
                            bgcolor: f.resolved ? '#e8f5e9' : '#fff3e0',
                            color: f.resolved ? '#2e7d32' : '#e65100'
                          }}
                        />
                      </Box>
                      <Typography variant="caption" color="text.secondary">{new Date(f.createdAt).toLocaleString('es-MX')}</Typography>
                    </Box>
                    {f.criterionDescription && (
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.3, fontStyle: 'italic' }}>
                        {f.criterionDescription}
                      </Typography>
                    )}
                    <Typography variant="body2">{f.note}</Typography>
                    {f.resolved ? (
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                        Resuelto por {f.resolvedByName || 'Sistema'} — {new Date(f.resolvedAt).toLocaleString('es-MX')}
                      </Typography>
                    ) : canViewQa && (
                      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 0.5 }}>
                        <Button
                          size="small"
                          startIcon={resolvingFindingCode === f.criteriaCode ? <CircularProgress size={12} /> : <FiberManualRecordIcon sx={{ fontSize: 12 }} />}
                          disabled={resolvingFindingCode === f.criteriaCode}
                          onClick={() => handleResolveFinding(f)}
                          sx={{ textTransform: 'none', fontSize: '0.72rem', py: 0.2 }}
                        >
                          Marcar como resuelto
                        </Button>
                      </Box>
                    )}
                  </Box>
                ))}
              </>
            );
          })()}
        </DialogContent>
        <DialogActions sx={{ px: 2, py: 1 }}>
          <Button onClick={() => setNotesDialogOpen(false)} size="small">Cerrar</Button>
        </DialogActions>
      </Dialog>

      {/* Historial de versiones reemplazadas de un RIC/PIPC -- cada reemplazo manual archiva la
          versión anterior en Dropbox antes de sobreescribir, ver ReportGenerationService.
          replaceDocumentManually. */}
      <Dialog open={versionsDialogOpen} onClose={() => setVersionsDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 1.5 }}>
          <Box>
            <Typography variant="subtitle1" fontWeight={600}>
              <HistoryIcon sx={{ fontSize: 18, verticalAlign: 'middle', mr: 1, color: '#5e35b1' }} />
              Versiones
            </Typography>
            {versionsDialogDoc && (
              <Typography variant="caption" color="text.secondary">
                {getDocumentKeyLabel(versionsDialogDoc.documentKey)} — {versionsDialogDoc.entityName} ({versionsDialogDoc.companyName})
              </Typography>
            )}
          </Box>
          <IconButton onClick={() => setVersionsDialogOpen(false)} size="small"><CloseIcon /></IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {versionsLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}><CircularProgress size={22} /></Box>
          ) : versions.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              Este documento no ha sido reemplazado todavía. Cada vez que se reemplace manualmente, la versión anterior queda archivada aquí.
            </Typography>
          ) : versions.map(v => (
            <Box key={v.id} sx={{ mb: 1.5, pb: 1.5, borderBottom: '1px solid #eee', display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1 }}>
              <Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.3 }}>
                  <Chip label={`Versión ${v.versionNumber}`} size="small" sx={{ bgcolor: '#ede7f6', color: '#4527a0', fontWeight: 600, fontSize: '0.68rem' }} />
                  {v.contentChanged === true && (
                    <Chip label="Modificado" size="small" sx={{ bgcolor: '#fff3e0', color: '#e65100', fontWeight: 600, fontSize: '0.68rem' }} />
                  )}
                </Box>
                <Typography variant="body2" fontWeight={600}>{v.originalFilename}</Typography>
                <Typography variant="caption" color="text.secondary">
                  Reemplazado por {v.replacedByName || 'Sistema'} — {new Date(v.replacedAt).toLocaleString('es-MX')}
                  {v.fileSize != null && ` — ${formatVersionFileSize(v.fileSize)}`}
                </Typography>
              </Box>
              {v.downloadLink ? (
                <Tooltip title="Descargar esta versión">
                  <Button
                    size="small"
                    startIcon={<CloudDownloadIcon fontSize="small" />}
                    component="a"
                    href={getVersionDownloadLink(v.downloadLink)}
                    sx={{ textTransform: 'none', flexShrink: 0 }}
                  >
                    Descargar
                  </Button>
                </Tooltip>
              ) : (
                <Typography variant="caption" color="text.secondary" sx={{ flexShrink: 0 }}>No disponible</Typography>
              )}
            </Box>
          ))}
        </DialogContent>
        <DialogActions sx={{ px: 2, py: 1 }}>
          <Button onClick={() => setVersionsDialogOpen(false)} size="small">Cerrar</Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default CentroGestionDocumental;
